const API_URL = process.env.REACT_APP_API_URL || '/api';

class ApiService {
  constructor() {
    this.accessToken = localStorage.getItem('accessToken');
    this.refreshToken = localStorage.getItem('refreshToken');
  }

  setTokens(accessToken, refreshToken) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
  }

  clearTokens() {
    this.accessToken = null;
    this.refreshToken = null;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  }

  async request(endpoint, options = {}) {
    const url = `${API_URL}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    try {
      let response = await fetch(url, { ...options, headers });

      // If token expired, try to refresh
      if (response.status === 401) {
        const data = await response.json();
        if (data.code === 'TOKEN_EXPIRED' && this.refreshToken) {
          const refreshed = await this.refreshAccessToken();
          if (refreshed) {
            headers['Authorization'] = `Bearer ${this.accessToken}`;
            response = await fetch(url, { ...options, headers });
          } else {
            this.clearTokens();
            window.location.href = '/';
            return null;
          }
        } else {
          throw new Error(data.message || 'Request failed');
        }
      }

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Request failed');
      }
      return data;
    } catch (err) {
      console.error('API Error:', err);
      throw err;
    }
  }

  async refreshAccessToken() {
    try {
      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: this.refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        this.accessToken = data.accessToken;
        localStorage.setItem('accessToken', data.accessToken);
        return true;
      }
      return false;
    } catch (err) {
      return false;
    }
  }

  // Auth
  async login(email, password) {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async loginWithGoogle(credential) {
    return this.request('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ credential }),
    });
  }

  async logout() {
    try {
      await this.request('/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ refreshToken: this.refreshToken }),
      });
    } catch (err) {
      // Ignore errors
    }
    this.clearTokens();
  }

  async getMe() {
    return this.request('/auth/me');
  }

  async changePassword(newPassword) {
    return this.request('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ newPassword }),
    });
  }

  async resetStudentPassword(id) {
    return this.request(`/admin/students/${id}/reset-password`, { method: 'POST' });
  }

  async resetDosenPassword(id) {
    return this.request(`/admin/dosens/${id}/reset-password`, { method: 'POST' });
  }

  // Admin endpoints
  async getStats() { return this.request('/admin/stats'); }
  async getStudents() { return this.request('/admin/students'); }
  async createStudent(data) { return this.request('/admin/students', { method: 'POST', body: JSON.stringify(data) }); }
  async updateStudent(id, data) { return this.request(`/admin/students/${id}`, { method: 'PUT', body: JSON.stringify(data) }); }
  async deleteStudent(id) { return this.request(`/admin/students/${id}`, { method: 'DELETE' }); }
  async bulkImportStudents(students) { return this.request('/admin/students/bulk', { method: 'POST', body: JSON.stringify({ students }) }); }
  async bulkDeleteStudents(ids) { return this.request('/admin/students/bulk-delete', { method: 'POST', body: JSON.stringify({ ids }) }); }

  async getDosens() { return this.request('/admin/dosens'); }
  async createDosen(data) { return this.request('/admin/dosens', { method: 'POST', body: JSON.stringify(data) }); }
  async updateDosen(id, data) { return this.request(`/admin/dosens/${id}`, { method: 'PUT', body: JSON.stringify(data) }); }
  async deleteDosen(id) { return this.request(`/admin/dosens/${id}`, { method: 'DELETE' }); }
  async bulkImportDosens(dosens) { return this.request('/admin/dosens/bulk', { method: 'POST', body: JSON.stringify({ dosens }) }); }
  async bulkDeleteDosens(ids) { return this.request('/admin/dosens/bulk-delete', { method: 'POST', body: JSON.stringify({ ids }) }); }

  async getCourses() { return this.request('/admin/courses'); }
  async createCourse(data) { return this.request('/admin/courses', { method: 'POST', body: JSON.stringify(data) }); }
  async updateCourse(id, data) { return this.request(`/admin/courses/${id}`, { method: 'PUT', body: JSON.stringify(data) }); }
  async deleteCourse(id) { return this.request(`/admin/courses/${id}`, { method: 'DELETE' }); }
  async bulkImportCourses(courses) { return this.request('/admin/courses/bulk', { method: 'POST', body: JSON.stringify({ courses }) }); }
  async bulkDeleteCourses(ids) { return this.request('/admin/courses/bulk-delete', { method: 'POST', body: JSON.stringify({ ids }) }); }

  async getPeriods() { return this.request('/admin/periods'); }
  async createPeriod(data) { return this.request('/admin/periods', { method: 'POST', body: JSON.stringify(data) }); }
  async updatePeriod(id, data) { return this.request(`/admin/periods/${id}`, { method: 'PUT', body: JSON.stringify(data) }); }
  async deletePeriod(id) { return this.request(`/admin/periods/${id}`, { method: 'DELETE' }); }

  async getEnrollments() { return this.request('/admin/enrollments'); }
  async createEnrollment(data) { return this.request('/admin/enrollments', { method: 'POST', body: JSON.stringify(data) }); }
  async deleteEnrollment(id) { return this.request(`/admin/enrollments/${id}`, { method: 'DELETE' }); }
  async bulkImportEnrollments(enrollments) { return this.request('/admin/enrollments/bulk', { method: 'POST', body: JSON.stringify({ enrollments }) }); }
  async bulkDeleteEnrollments(ids) { return this.request('/admin/enrollments/bulk-delete', { method: 'POST', body: JSON.stringify({ ids }) }); }

  async getTeachings() { return this.request('/admin/teachings'); }
  async createTeaching(data) { return this.request('/admin/teachings', { method: 'POST', body: JSON.stringify(data) }); }
  async deleteTeaching(id) { return this.request(`/admin/teachings/${id}`, { method: 'DELETE' }); }
  async bulkImportTeachings(teachings) { return this.request('/admin/teachings/bulk', { method: 'POST', body: JSON.stringify({ teachings }) }); }
  async bulkDeleteTeachings(ids) { return this.request('/admin/teachings/bulk-delete', { method: 'POST', body: JSON.stringify({ ids }) }); }

  async getAssessments(params = {}) {
    const qs = new URLSearchParams(Object.entries(params).filter(([,v]) => v !== '' && v != null)).toString();
    return this.request(`/admin/assessments${qs ? `?${qs}` : ''}`);
  }
  async deleteAssessment(id) { return this.request(`/admin/assessments/${id}`, { method: 'DELETE' }); }

  // Semester endpoints
  async getSemesters() { return this.request('/kelas/semesters'); }
  async createSemester(data) { return this.request('/kelas/semesters', { method: 'POST', body: JSON.stringify(data) }); }
  async updateSemester(id, data) { return this.request(`/kelas/semesters/${id}`, { method: 'PUT', body: JSON.stringify(data) }); }
  async deleteSemester(id) { return this.request(`/kelas/semesters/${id}`, { method: 'DELETE' }); }
  async bulkImportSemesters(semesters) { return this.request('/kelas/semesters/bulk', { method: 'POST', body: JSON.stringify({ semesters }) }); }

  // Kelas endpoints
  async getKelas() { return this.request('/kelas/kelas'); }
  async createKelas(data) { return this.request('/kelas/kelas', { method: 'POST', body: JSON.stringify(data) }); }
  async updateKelas(id, data) { return this.request(`/kelas/kelas/${id}`, { method: 'PUT', body: JSON.stringify(data) }); }
  async deleteKelas(id) { return this.request(`/kelas/kelas/${id}`, { method: 'DELETE' }); }
  async bulkImportKelas(kelasList) { return this.request('/kelas/kelas/bulk', { method: 'POST', body: JSON.stringify({ kelasList }) }); }

  // Kelas-Mahasiswa endpoints
  async getKelasMahasiswa(kelasId) { return this.request(`/kelas/kelas/${kelasId}/mahasiswa`); }
  async addMahasiswaToKelas(kelasId, studentId) { return this.request(`/kelas/kelas/${kelasId}/mahasiswa`, { method: 'POST', body: JSON.stringify({ studentId }) }); }
  async removeMahasiswaFromKelas(kmId) { return this.request(`/kelas/kelas-mahasiswa/${kmId}`, { method: 'DELETE' }); }

  // Kelas-MataKuliah endpoints
  async getKelasMataKuliah(kelasId) { return this.request(`/kelas/kelas/${kelasId}/mata-kuliah`); }
  async getAllKelasMataKuliah() { return this.request('/kelas/kelas-mata-kuliah'); }
  async assignMKToKelas(kelasId, data) { return this.request(`/kelas/kelas/${kelasId}/mata-kuliah`, { method: 'POST', body: JSON.stringify(data) }); }
  async removeMKFromKelas(kmkId) { return this.request(`/kelas/kelas-mata-kuliah/${kmkId}`, { method: 'DELETE' }); }

  // ============================================
  // ANALYTICS
  // ============================================
  async getAnalyticsPeriods() { return this.request('/analytics/periods'); }
  async getAnalyticsTrend(params) { return this.request(`/analytics/trend?${new URLSearchParams(params)}`); }
  async getAnalyticsCompare(params) { return this.request(`/analytics/compare?${new URLSearchParams(params)}`); }
  async getAnalyticsAtRisk(params = {}) { return this.request(`/analytics/at-risk?${new URLSearchParams(params)}`); }
  async getAnalyticsSettings() { return this.request('/analytics/settings'); }
  async updateAnalyticsSettings(data) { return this.request('/analytics/settings', { method: 'PUT', body: JSON.stringify(data) }); }

  async getAdmins() { return this.request('/admin/admins'); }
  async getAuditLogs(params = {}) { return this.request(`/admin/audit-logs?${new URLSearchParams(params)}`); }
  async createAdmin(data) { return this.request('/admin/admins', { method: 'POST', body: JSON.stringify(data) }); }
  async updateAdmin(id, data) { return this.request(`/admin/admins/${id}`, { method: 'PUT', body: JSON.stringify(data) }); }
  async updateAdminProfile(data) { return this.request('/admin/admins/me', { method: 'PUT', body: JSON.stringify(data) }); }
  async deleteAdmin(id) { return this.request(`/admin/admins/${id}`, { method: 'DELETE' }); }
  async resetAdminPassword(id) { return this.request(`/admin/admins/${id}/reset-password`, { method: 'POST' }); }

  // Dosen endpoints
  async getDosenActivePeriod() { return this.request('/dosen/active-period'); }
  async getDosenCourses() { return this.request('/dosen/my-courses'); }
  async getCourseStudents(courseId, prodi = '') { return this.request(`/dosen/course/${courseId}/students${prodi ? `?prodi=${encodeURIComponent(prodi)}` : ''}`); }
  async getProgramStudi() { return this.request('/dosen/program-studi'); }
  async checkDosenAssessment(studentId, courseId, formType, periodId) { 
    return this.request(`/dosen/assessment/check?studentId=${studentId}&courseId=${courseId}&formType=${formType}&periodId=${periodId}`); 
  }
  async submitDosenAssessment(data) { return this.request('/dosen/assessment', { method: 'POST', body: JSON.stringify(data) }); }

  // Mahasiswa endpoints
  async getMahasiswaActivePeriod() { return this.request('/mahasiswa/active-period'); }
  async getMahasiswaCourses() { return this.request('/mahasiswa/my-courses'); }
  async checkMahasiswaAssessment(courseId, formType, periodId) { 
    return this.request(`/mahasiswa/assessment/check?courseId=${courseId}&formType=${formType}&periodId=${periodId}`); 
  }
  async submitMahasiswaAssessment(data) { return this.request('/mahasiswa/assessment', { method: 'POST', body: JSON.stringify(data) }); }
  async getMyAssessments() { return this.request('/mahasiswa/my-assessments'); }
  async updateMahasiswaProfile(data) { return this.request('/mahasiswa/me', { method: 'PUT', body: JSON.stringify(data) }); }

  // Notifications
  async getNotifications() { return this.request('/notifications'); }
  async markNotificationRead(id) { return this.request(`/notifications/${id}/read`, { method: 'PUT' }); }
}

const api = new ApiService();
export default api;
